import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { Search, Filter, ExternalLink, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Affiliate LifeWave Shop Link
const LIFEWAVE_SHOP_URL = 'https://lifewave.com/ioanaalexandra/store/products';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Seed data first
        await axios.post(`${API_URL}/api/seed`);
        const response = await axios.get(`${API_URL}/api/products`);
        setProducts(response.data);
        setFilteredProducts(response.data);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(response.data.map(p => p.category))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    let result = products;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery) {
      result = result.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredProducts(result);
  }, [searchQuery, selectedCategory, products]);

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <p className="text-sky-600 font-semibold uppercase tracking-wide text-sm mb-2">{t('discover')}</p>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t('lifewaveProducts')}</h1>
          <p className="text-slate-600 max-w-2xl">
            {t('productsPageDesc')}
          </p>
        </div>

        {/* Affiliate Banner */}
        <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl p-6 mb-6 shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <h2 className="text-xl font-bold mb-1">{t('shopMyStore')}</h2>
              <p className="text-sky-100 text-sm">{t('purchaseDirectly')}</p>
            </div>
            <Button
              className="bg-white text-sky-600 hover:bg-sky-50 rounded-full px-8 py-3 font-semibold shadow-md"
              onClick={() => window.open(LIFEWAVE_SHOP_URL, '_blank')}
              data-testid="shop-lifewave-banner"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              {t('visitShop')}
            </Button>
          </div>
        </div>

        {/* Price Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">{t('importantPricing')}</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>{t('priceDisclaimer1')}</li>
                <li>{t('priceDisclaimer2')}</li>
                <li>{t('priceDisclaimer3')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder={t('searchProducts')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                data-testid="product-search"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Filter className="w-5 h-5 text-slate-400" />
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                  className={selectedCategory === 'all' ? 'bg-sky-600 hover:bg-sky-700' : ''}
                  data-testid="filter-all"
                >
                  {t('all')}
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category ? 'bg-sky-600 hover:bg-sky-700' : ''}
                    data-testid={`filter-${category.toLowerCase()}`}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                <div className="aspect-square bg-slate-200 rounded-xl mb-4"></div>
                <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
                <div className="h-6 bg-slate-200 rounded w-2/3 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">{t('noProductsFound')}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
            >
              {t('clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Results Count */}
        <div className="mt-8 text-center text-slate-500">
          {t('showing')} {filteredProducts.length} {t('of')} {products.length} {t('productsText')}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <Button
            size="lg"
            className="bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white rounded-full px-12 py-6 text-lg shadow-lg"
            onClick={() => window.open(LIFEWAVE_SHOP_URL, '_blank')}
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            {t('shopAllProducts')}
          </Button>
          <p className="text-slate-500 text-sm mt-3">
            {t('purchaseSecurely')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
